/**
 * The task engine: what may happen to a card, and what happens because of it.
 *
 * Every change to an instance goes through `act()`, which returns either a
 * refusal -- a sentence, as a value, never a throw (utucat learned that a
 * throw inside a Durable Object is reported twice) -- or the new instance
 * together with:
 *
 * - `events`: the audit entries for what changed, one per fact. The store
 *   writes these in the same transaction as the instance, so there is no state
 *   change without its audit row and no audit row without its change (PRD G5).
 * - `effects`: who has to be told. Nothing here sends anything; the worker
 *   turns effects into mail and in-app notices after the write commits.
 *
 * After every action the instance is `settle()`d: cards whose hard
 * dependencies are all finished become unblocked, their owners are told, and
 * notify cards whose turn has come fire and finish themselves. That loop is
 * PRD goal G1 -- no task moves without an explicit notification -- and it
 * lives in exactly one place.
 *
 * The engine does not read the clock. `now` is passed in, which is what lets
 * every timing rule be tested without waiting for it.
 */

import {
  checklistOf,
  conditionHolds,
  recordedConditions,
  recordedValue,
  type EscalationLevel,
  type Instance,
  type Note,
  type Task,
  type TaskState,
} from "./instance.js";
import { differsFromIssued } from "./issued.js";
import { CHOICE_SEPARATOR, choicesOf, type TemplateNode } from "./template.js";

export type Role = "admin" | "hr" | "manager" | "member" | "compliance";

export interface Actor {
  id: string;
  role: Role;
  department: string;
}

/** The pseudo-actor for what the engine does by itself: unblocking, notify cards firing. */
export const SYSTEM = "system";

export type AuditAction =
  | "state"
  | "subtask"
  | "field"
  | "assign"
  | "escalate"
  | "poke"
  | "notify"
  | "signoff"
  | "due"
  | "note"
  | "comment"
  | "mismatch"
  | "template"
  | "pii_view"
  | "erase"
  | "department";

export interface AuditEvent {
  at: number;
  actor: string;
  action: AuditAction;
  task: string;
  /** What it was and what it became, as text, so the log reads the same whatever the action was. */
  from: string | null;
  to: string | null;
  /** A reason, where one was required: why it was blocked, why it was skipped. */
  note?: string;
}

export interface Effect {
  type: "notify";
  task: string;
  /** User ids. Empty when nobody is assigned: the worker then tells the department's managers instead. */
  to: string[];
  department: string;
  why:
    | "your-turn"
    | "signoff-wanted"
    | "handoff"
    | "poke"
    | "mentioned"
    | "commented"
    | "reminder"
    | "overdue"
    | "critical"
    | "reassigned"
    | "blocked"
    | "rescheduled"
    | "changed"
    | "cancelled";
  urgent: boolean;
  /** Notify cards only: an address outside the organisation to mail as well (`TemplateNode.mailTo`). */
  mailTo?: string;
  /**
   * An overdue notice for a time-critical card: HR hears with the department's
   * managers, at once, rather than only when nobody above the assignee is left
   * (PRD 9.3: a late access revocation is nobody's to sit on).
   */
  alsoHr?: true;
}

export type Action =
  | { type: "start" }
  | { type: "complete"; values?: Record<string, string>; reviewedBy?: string }
  | { type: "tick"; index: number; done: boolean }
  | { type: "set"; field: string; value: string }
  | { type: "block"; reason: string }
  | { type: "resume" }
  | { type: "skip"; reason: string }
  | { type: "reassign"; assigneeId: string | null; backupId?: string | null }
  | { type: "escalate"; note?: string }
  | { type: "signoff"; department: string }
  | { type: "unsign"; department: string }
  | { type: "note"; domain: string; text: string; link?: string | null }
  | { type: "unnote"; domain: string };

export type Outcome =
  { ok: true; instance: Instance; events: AuditEvent[]; effects: Effect[] } | { ok: false; reason: string };

const OPEN: readonly TaskState[] = ["unblocked", "in_progress", "blocked"];
const FINISHED: readonly TaskState[] = ["completed", "skipped"];

export const isOpen = (task: Task): boolean => OPEN.includes(task.state);
export const isFinished = (task: Task): boolean => FINISHED.includes(task.state);

export const NOTE_DOMAIN_LENGTH = 120;

/** "Billing" and "billing " are one domain: a second note on it replaces the first. */
const sameDomain = (note: Note, domain: string): boolean =>
  note.domain.toLowerCase() === domain.toLowerCase();

/** A link somebody can click: http or https, something after it, and no spaces. */
const isWebLink = (value: string): boolean =>
  (value.startsWith("http://") || value.startsWith("https://")) && value.length > 8 && !value.includes(" ");

const refuse = (reason: string): Outcome => ({ ok: false, reason });

/** The ids to tell about a card: its assignee, or nobody (and the worker falls back to managers). */
const owners = (task: Task): string[] => (task.assigneeId ? [task.assigneeId] : []);

/* ----------------------------------------------------------- who may do what -- */

/** Doing the work: the assignee, the backup, HR and admins -- and anyone in the department while nobody holds it. */
export function canWork(actor: Actor, task: Task): boolean {
  if (actor.role === "compliance") return false;
  if (actor.role === "admin" || actor.role === "hr") return true;
  if (actor.id === task.assigneeId || actor.id === task.backupId) return true;
  return task.assigneeId === null && actor.department === task.department;
}

/** Overriding the plan: skipping and reassigning are a manager's, HR's or an admin's (PRD 7.1, 7.4). */
export function canDirect(actor: Actor): boolean {
  return actor.role === "admin" || actor.role === "hr" || actor.role === "manager";
}

/**
 * Signing off for a department: somebody in it, or HR and admins on its
 * behalf. Deliberately not the same as `canWork()` -- Finance approving the
 * order does not make the order Finance's to fill in.
 */
export function canSignOff(actor: Actor, task: Task, department: string): boolean {
  if (!(department in task.signoffs)) return false;
  if (actor.role === "compliance") return false;
  return actor.role === "admin" || actor.role === "hr" || actor.department === department;
}

/** Departments whose sign-off a card is still waiting for. */
export function signoffsWaiting(task: Task): string[] {
  return Object.entries(task.signoffs)
    .filter(([, signed]) => signed === null)
    .map(([department]) => department);
}

/* ------------------------------------------------------------------- reading -- */

/** Hard predecessors not yet finished: while any remain, the card cannot start. */
export function waitingOn(instance: Instance, key: string): string[] {
  return instance.edges
    .filter((edge) => edge.to === key && edge.kind === "hard")
    .map((edge) => edge.from)
    .filter((from) => !isFinished(instance.tasks[from]!));
}

/** Soft predecessors not yet finished: the card may start, with the warning PRD 7.2 asks for. */
export function softWaitingOn(instance: Instance, key: string): string[] {
  return instance.edges
    .filter((edge) => edge.to === key && edge.kind === "soft")
    .map((edge) => edge.from)
    .filter((from) => !isFinished(instance.tasks[from]!));
}

export type InputValue =
  { state: "ready"; value: string } | { state: "pending"; from: string } | { state: "absent"; from: string };

/**
 * What a linked field shows: the value, "Pending [card]" while the source is
 * unfinished (PRD 6.5), or absent when the source finished without one -- a
 * skipped card, or an optional field left blank.
 */
export function inputValue(instance: Instance, link: { node: string; field: string }): InputValue {
  const source = instance.tasks[link.node]!;
  const value = source.values[link.field];
  if (source.state === "completed" && value !== undefined && value !== "") return { state: "ready", value };
  if (isFinished(source)) return { state: "absent", from: link.node };
  return { state: "pending", from: link.node };
}

/* ------------------------------------------------------------------- settling -- */

/** Why a card that waits on nothing has not opened yet: its day has not come. Null when it can open. */
export function notYet(task: Task, now: number): string | null {
  return task.notBeforeAt !== undefined && task.notBeforeAt > now && task.notBeforeDay
    ? `This opens on ${task.notBeforeDay}.`
    : null;
}

/**
 * The value that rules a card out, as the reason it was skipped, once every
 * card it branches on is finished; null when it applies.
 */
function branchedOut(instance: Instance, key: string): string | null {
  const node = instance.nodes[key]!;
  for (const condition of recordedConditions(node)) {
    const value = recordedValue(instance, condition.recorded) ?? "";
    if (conditionHolds(condition, instance.profile, value)) continue;
    const source = instance.nodes[condition.recorded.node];
    const label =
      source?.fields?.find((field) => field.key === condition.recorded.field)?.label ??
      condition.recorded.field;
    return value
      ? `Does not apply: "${label}" was ${choicesOf(value).join(", ")}.`
      : `Does not apply: "${label}" was not recorded.`;
  }
  // A checklist of choices with none made, and nothing of its own, is nothing to do.
  if (node.checklistFrom && !node.subtasks?.length && chosenFor(instance, node).length === 0)
    return `Nothing to do: nothing was chosen for "${fieldLabel(instance, node.checklistFrom)}".`;
  return null;
}

const fieldLabel = (instance: Instance, link: { node: string; field: string }): string =>
  instance.nodes[link.node]?.fields?.find((field) => field.key === link.field)?.label ?? link.field;

/** The choices another card made that this card's checklist is drawn from; none when it has no such checklist. */
function chosenFor(instance: Instance, node: TemplateNode): string[] {
  return node.checklistFrom ? choicesOf(recordedValue(instance, node.checklistFrom)) : [];
}

/**
 * Unblock what can be unblocked and fire what has to fire, until nothing
 * changes. Loops because a notify card finishing can unblock the next card,
 * which may itself be a notify card.
 *
 * A card whose day has not come stays shut (`notYet()`); the alarm settles
 * again on that day. A card that branches on a recorded value and does not
 * match it is skipped here, by the system, with the value as the reason, and
 * nobody is told: it is work this person is not getting.
 */
export function settle(
  instance: Instance,
  now: number,
): { instance: Instance; events: AuditEvent[]; effects: Effect[] } {
  const tasks = { ...instance.tasks };
  const events: AuditEvent[] = [];
  const effects: Effect[] = [];
  const current = (): Instance => ({ ...instance, tasks });

  let changed = true;
  while (changed) {
    changed = false;
    for (const key of instance.order) {
      const task = tasks[key]!;
      if (task.state !== "not_started" || waitingOn(current(), key).length > 0) continue;

      const out = branchedOut(current(), key);
      if (out) {
        tasks[key] = { ...task, state: "skipped", skipReason: out, finishedAt: now, finishedBy: SYSTEM };
        events.push({
          at: now,
          actor: SYSTEM,
          action: "state",
          task: key,
          from: "not_started",
          to: "skipped",
          note: out,
        });
        changed = true;
        continue;
      }
      if (notYet(task, now)) continue;

      const node = instance.nodes[key]!;
      if (node.kind === "notify") {
        tasks[key] = { ...task, state: "completed", startedAt: now, finishedAt: now, finishedBy: SYSTEM };
        events.push({
          at: now,
          actor: SYSTEM,
          action: "notify",
          task: key,
          from: "not_started",
          to: "completed",
        });
        effects.push({
          type: "notify",
          task: key,
          to: owners(task),
          department: task.department,
          why: "handoff",
          urgent: node.urgency === "urgent",
          ...(node.mailTo ? { mailTo: node.mailTo } : {}),
        });
      } else {
        // The lines chosen on an earlier card join the checklist as it opens, unticked.
        const chosen = chosenFor(current(), node);
        tasks[key] = {
          ...task,
          state: "unblocked",
          ...(chosen.length
            ? { chosenLines: chosen, subtasksDone: [...task.subtasksDone, ...chosen.map(() => false)] }
            : {}),
        };
        events.push({
          at: now,
          actor: SYSTEM,
          action: "state",
          task: key,
          from: "not_started",
          to: "unblocked",
        });
        effects.push({
          type: "notify",
          task: key,
          to: owners(task),
          department: task.department,
          why: "your-turn",
          urgent: node.timeCritical === true,
        });
        // Everybody gating the card hears at the same moment as its owner, so
        // a sign-off is never the thing discovered last.
        for (const department of node.signoffs ?? []) {
          effects.push({
            type: "notify",
            task: key,
            to: [],
            department,
            why: "signoff-wanted",
            urgent: node.timeCritical === true,
          });
        }
      }
      changed = true;
    }
  }
  return { instance: current(), events, effects };
}

/**
 * The cards finishing this one would open, in template order -- notices that
 * would fire included, and whatever those open in turn. Read by settling a
 * copy with this card finished, so it is exactly what `settle()` will do and
 * never a second account of the rules. Empty for a finished card. No clock
 * is asked for: settling at the beginning of time leaves out a card with a
 * day of its own to open on, which is right, since it does not open when
 * this one finishes. A card that would skip itself is not opened either.
 */
export function opensNext(instance: Instance, key: string): string[] {
  const task = instance.tasks[key];
  if (!task || isFinished(task)) return [];
  const finished: Instance = {
    ...instance,
    tasks: { ...instance.tasks, [key]: { ...task, state: "completed" } },
  };
  const after = settle(finished, 0).instance;
  return instance.order.filter(
    (other) =>
      other !== key &&
      instance.tasks[other]!.state === "not_started" &&
      after.tasks[other]!.state !== "not_started" &&
      after.tasks[other]!.state !== "skipped",
  );
}

/* ---------------------------------------------------------------------- acting -- */

function completionProblem(
  instance: Instance,
  node: TemplateNode,
  task: Task,
  reviewedBy: string | undefined,
): string | null {
  if (task.subtasksDone.some((done) => !done)) return "Tick every line of the checklist first.";
  for (const field of node.fields ?? []) {
    if (field.required && !(task.values[field.key] ?? "").trim())
      return `"${field.label}" has to be filled in first.`;
    const value = task.values[field.key];
    const stranger = field.options
      ? (field.multiple ? choicesOf(value) : value ? [value] : []).find(
          (choice) => !field.options!.includes(choice),
        )
      : undefined;
    if (stranger !== undefined) return `"${stranger}" is not one of the choices for "${field.label}".`;
  }
  for (const link of node.inputs ?? []) {
    const value = inputValue(instance, link);
    if (value.state === "pending")
      return `This needs "${instance.nodes[link.node]!.title}" to be finished first.`;
  }
  const waiting = signoffsWaiting(task);
  if (waiting.length > 0) return `This still needs a sign-off from ${waiting.join(" and ")}.`;
  if (node.compliance && instance.profile.hipaa && !reviewedBy?.trim()) {
    return "This site is under HIPAA, so a compliance task needs the name of whoever reviewed it.";
  }
  return null;
}

/**
 * Do one thing to one card.
 *
 * `start` and `complete` are deliberately loose about order: completing an
 * unblocked card that was never started records the start too. The PRD's
 * state table has In Progress as a required stop, but making an IT tech press
 * "Start" on a thirty-second task before pressing "Done" is the kind of tax
 * illgati's direction note is about -- see docs/notes/decisions.md.
 */
export function act(instance: Instance, key: string, action: Action, actor: Actor, now: number): Outcome {
  const task = instance.tasks[key];
  const node = instance.nodes[key];
  if (!task || !node) return refuse("That task is not part of this workflow.");
  if (node.kind === "notify") return refuse("A notify card sends itself when its turn comes.");

  const signing = action.type === "signoff" || action.type === "unsign";
  if (signing && !canSignOff(actor, task, action.department)) {
    return refuse(
      action.department in task.signoffs
        ? `Only somebody in ${action.department} can sign off for ${action.department}.`
        : `This task does not need a sign-off from ${action.department}.`,
    );
  }
  const directing = action.type === "skip" || action.type === "reassign";
  if (!signing && (directing ? !canDirect(actor) : !canWork(actor, task))) {
    return refuse(
      directing ? "Only a manager, HR or an admin can do that." : "This task is not yours to work on.",
    );
  }

  const events: AuditEvent[] = [];
  const effects: Effect[] = [];
  const log = (event: Omit<AuditEvent, "at" | "actor">) =>
    events.push({ at: now, actor: actor.id, ...event });
  let next: Task = task;

  switch (action.type) {
    case "start": {
      if (task.state === "not_started") return refuse(notOpenYet(instance, key, now));
      if (task.state !== "unblocked") return refuse(stateSentence(task.state));
      next = { ...task, state: "in_progress", startedAt: now };
      log({ action: "state", task: key, from: task.state, to: "in_progress" });
      break;
    }

    case "tick": {
      if (!isOpen(task)) return refuse(stateSentence(task.state));
      if (!Number.isInteger(action.index) || action.index < 0 || action.index >= task.subtasksDone.length) {
        return refuse("That checklist line does not exist.");
      }
      if (task.subtasksDone[action.index] === action.done)
        return { ok: true, instance, events: [], effects: [] };
      const subtasksDone = task.subtasksDone.map((done, index) =>
        index === action.index ? action.done : done,
      );
      next = { ...task, subtasksDone };
      log({
        action: "subtask",
        task: key,
        from: String(!action.done),
        to: String(action.done),
        note: checklistOf(node, task)[action.index] ?? "",
      });
      break;
    }

    case "set": {
      if (!isOpen(task)) return refuse(stateSentence(task.state));
      const field = node.fields?.find((candidate) => candidate.key === action.field);
      if (!field) return refuse("That task does not record that.");
      const offered = field.options;
      const given = field.multiple ? choicesOf(action.value) : action.value === "" ? [] : [action.value];
      const stranger = offered ? given.find((choice) => !offered.includes(choice)) : undefined;
      if (stranger !== undefined)
        return refuse(`"${stranger}" is not one of the choices for "${field.label}".`);
      // Several choices are kept in the order they are offered, so the same set is always the same value.
      const value =
        field.multiple && offered
          ? offered.filter((choice) => given.includes(choice)).join(CHOICE_SEPARATOR)
          : action.value;
      const before = task.values[field.key] ?? null;
      if (before === value) return { ok: true, instance, events: [], effects: [] };
      next = { ...task, values: { ...task.values, [field.key]: value } };
      // A PII value's before and after never enter the log; that it changed does (PRD 11.2).
      log({
        action: "field",
        task: key,
        from: field.pii ? (before === null ? null : "[PII]") : before,
        to: field.pii ? "[PII]" : value,
        note: field.key,
      });
      // What came back is not what went out: the card says so, and so does the log.
      const issued = field.returns ? instance.issued?.find((item) => item.kind === field.returns) : undefined;
      if (issued && differsFromIssued(value, issued))
        log({ action: "mismatch", task: key, from: issued.value, to: value, note: field.label });
      break;
    }

    case "complete": {
      if (task.state === "not_started") return refuse(notOpenYet(instance, key, now));
      if (task.state === "blocked")
        return refuse("This is flagged as blocked. Clear the block before finishing it.");
      if (!isOpen(task)) return refuse(stateSentence(task.state));

      let values = task.values;
      for (const [field, value] of Object.entries(action.values ?? {})) {
        const outcome = act(
          { ...instance, tasks: { ...instance.tasks, [key]: { ...task, values } } },
          key,
          { type: "set", field, value },
          actor,
          now,
        );
        if (!outcome.ok) return outcome;
        values = outcome.instance.tasks[key]!.values;
        // Only the field entry: anything the nested call settled is settled
        // again, once, at the end of this one.
        events.push(
          ...outcome.events.filter(
            (event) => event.task === key && (event.action === "field" || event.action === "mismatch"),
          ),
        );
      }

      const draft: Task = { ...task, values };
      const problem = completionProblem(instance, node, draft, action.reviewedBy);
      if (problem) return refuse(problem);

      next = {
        ...draft,
        state: "completed",
        startedAt: task.startedAt ?? now,
        finishedAt: now,
        finishedBy: actor.id,
      };
      log({
        action: "state",
        task: key,
        from: task.state,
        to: "completed",
        ...(action.reviewedBy?.trim() ? { note: `Reviewed by ${action.reviewedBy.trim()}` } : {}),
      });
      break;
    }

    case "block": {
      if (task.state !== "in_progress" && task.state !== "unblocked")
        return refuse(stateSentence(task.state));
      if (!action.reason.trim()) return refuse("Say what is in the way, so whoever reads this can help.");
      next = { ...task, state: "blocked", blockedReason: action.reason.trim() };
      log({ action: "state", task: key, from: task.state, to: "blocked", note: action.reason.trim() });
      effects.push({
        type: "notify",
        task: key,
        to: [],
        department: task.department,
        why: "blocked",
        urgent: node.timeCritical === true,
      });
      break;
    }

    case "resume": {
      if (task.state !== "blocked") return refuse("Only a blocked task can be picked back up.");
      next = { ...task, state: "in_progress", blockedReason: null, startedAt: task.startedAt ?? now };
      log({ action: "state", task: key, from: "blocked", to: "in_progress" });
      break;
    }

    case "skip": {
      if (isFinished(task)) return refuse(stateSentence(task.state));
      if (node.gate) {
        const held = instance.edges.filter((edge) => edge.from === key).map((edge) => edge.to);
        return refuse(
          `This is a gate, so it cannot be skipped: ${titles(instance, held)} would open without the check. ` +
            "Finish it, or cancel the whole workflow if it no longer applies.",
        );
      }
      if (!action.reason.trim()) return refuse("A skip needs a reason; it goes in the audit log.");
      next = {
        ...task,
        state: "skipped",
        skipReason: action.reason.trim(),
        finishedAt: now,
        finishedBy: actor.id,
      };
      log({ action: "state", task: key, from: task.state, to: "skipped", note: action.reason.trim() });
      break;
    }

    case "reassign": {
      if (isFinished(task)) return refuse(stateSentence(task.state));
      const backupId = action.backupId === undefined ? task.backupId : action.backupId;
      if (action.assigneeId === task.assigneeId && backupId === task.backupId)
        return { ok: true, instance, events: [], effects: [] };
      next = { ...task, assigneeId: action.assigneeId, backupId };
      log({ action: "assign", task: key, from: task.assigneeId, to: action.assigneeId });
      if (action.assigneeId && action.assigneeId !== task.assigneeId && task.state !== "not_started") {
        effects.push({
          type: "notify",
          task: key,
          to: [action.assigneeId],
          department: task.department,
          why: "reassigned",
          urgent: node.timeCritical === true,
        });
      }
      break;
    }

    case "signoff":
    case "unsign": {
      if (!isOpen(task)) return refuse(stateSentence(task.state));
      const signed = action.type === "signoff";
      if ((task.signoffs[action.department] !== null) === signed)
        return { ok: true, instance, events: [], effects: [] };
      next = {
        ...task,
        signoffs: { ...task.signoffs, [action.department]: signed ? { by: actor.id, at: now } : null },
      };
      log({
        action: "signoff",
        task: key,
        from: signed ? null : "signed",
        to: signed ? "signed" : null,
        note: action.department,
      });
      // The owner is told when the last sign-off they were waiting on arrives.
      if (signed && signoffsWaiting(next).length === 0) {
        effects.push({
          type: "notify",
          task: key,
          to: owners(task),
          department: task.department,
          why: "your-turn",
          urgent: node.timeCritical === true,
        });
      }
      break;
    }

    case "note": {
      if (!node.notes) return refuse("This card does not take notes.");
      if (!isOpen(task)) return refuse(stateSentence(task.state));
      const domain = action.domain.trim();
      if (!domain) return refuse("Say what the note is about: a system, a process, a relationship.");
      if (domain.length > NOTE_DOMAIN_LENGTH)
        return refuse(`Keep the name of what it is about under ${NOTE_DOMAIN_LENGTH} characters.`);
      if (!action.text.trim()) return refuse("A note needs something in it.");
      const link = action.link?.trim() || null;
      if (link && !isWebLink(link)) return refuse("A link has to start with http:// or https://.");
      const before = (task.notes ?? []).find((note) => sameDomain(note, domain));
      const note: Note = { domain, text: action.text.trim(), link, by: actor.id, at: now };
      if (before && before.text === note.text && before.link === link)
        return { ok: true, instance, events: [], effects: [] };
      next = {
        ...task,
        notes: [...(task.notes ?? []).filter((other) => !sameDomain(other, domain)), note],
      };
      // That a note changed, and what it is about -- never what it says.
      log({
        action: "note",
        task: key,
        from: before ? "edited" : null,
        to: before ? "edited" : "added",
        note: domain,
      });
      break;
    }

    case "unnote": {
      if (!isOpen(task)) return refuse(stateSentence(task.state));
      const domain = action.domain.trim();
      if (!(task.notes ?? []).some((note) => sameDomain(note, domain)))
        return { ok: true, instance, events: [], effects: [] };
      next = { ...task, notes: (task.notes ?? []).filter((note) => !sameDomain(note, domain)) };
      log({ action: "note", task: key, from: "added", to: "removed", note: domain });
      break;
    }

    case "escalate": {
      if (!isOpen(task)) return refuse(stateSentence(task.state));
      if (task.escalation >= 3) return refuse("This is already in HR's critical queue.");
      // A person escalating goes straight to the department head (level 2),
      // and a second time to HR's critical queue (3). Level 1 is only ever
      // the automatic reminder.
      const to: EscalationLevel = task.escalation < 2 ? 2 : 3;
      next = { ...task, escalation: to };
      log({
        action: "escalate",
        task: key,
        from: String(task.escalation),
        to: String(next.escalation),
        ...(action.note ? { note: action.note } : {}),
      });
      effects.push({
        type: "notify",
        task: key,
        to: [],
        department: task.department,
        why: next.escalation === 3 ? "critical" : "overdue",
        urgent: true,
      });
      break;
    }
  }

  const changed: Instance = { ...instance, tasks: { ...instance.tasks, [key]: next } };
  const settled = settle(changed, now);
  return {
    ok: true,
    instance: settled.instance,
    events: [...events, ...settled.events],
    effects: [...effects, ...settled.effects],
  };
}

/* --------------------------------------------------------------------- words -- */

/** Why a card that has not opened cannot be worked yet: what it waits on, or the day it opens. */
function notOpenYet(instance: Instance, key: string, now: number): string {
  const waiting = waitingOn(instance, key);
  if (waiting.length > 0) return `This is waiting on ${titles(instance, waiting)}.`;
  return notYet(instance.tasks[key]!, now) ?? "This task is still waiting on an earlier one.";
}

function titles(instance: Instance, keys: readonly string[]): string {
  const names = keys.map((key) => `"${instance.nodes[key]?.title ?? key}"`);
  if (names.length <= 1) return names[0] ?? "an earlier task";
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

function stateSentence(state: TaskState): string {
  switch (state) {
    case "not_started":
      return "This task is still waiting on an earlier one.";
    case "unblocked":
      return "This task has not been started.";
    case "in_progress":
      return "This task is already in progress.";
    case "blocked":
      return "This task is flagged as blocked.";
    case "completed":
      return "This task is finished, and a finished task does not change.";
    case "skipped":
      return "This task was skipped, and a skipped task does not change.";
  }
}
